import React, { useState } from 'react';
import { FileText, AlertTriangle, CheckCircle, Copy } from 'lucide-react';
import { cleanExclusionItems } from '../../lib/exclusions';

// Exclusions View Component - Lists all exclusions from the proposal
const ExclusionsView = ({ exclusionsData }) => {
    const [copied, setCopied] = useState(false);
    
    // Group exclusions by scope (bare "EXCLUDES:" labels filtered out)
    const groupedExclusions = (exclusionsData?.exclusions_by_scope || [])
        .map(g => ({ ...g, exclusions: cleanExclusionItems(g.exclusions) }))
        .filter(g => g.exclusions.length > 0);
    const generalExclusions = cleanExclusionItems(exclusionsData?.general_exclusions);
    
    // Generate email-formatted content for exclusions
    const generateExclusionsEmailContent = () => {
        let content = [];
        
        content.push('• PROPOSAL EXCLUSIONS');
        content.push('');
        
        // General exclusions first
        if (generalExclusions.length > 0) {
            content.push('    • GENERAL EXCLUSIONS:');
            generalExclusions.forEach((exc) => {
                content.push(`        - ${exc}`);
            });
            content.push('');
        }
        
        // Scope-specific exclusions
        groupedExclusions.forEach((scopeGroup) => {
            if (scopeGroup.exclusions && scopeGroup.exclusions.length > 0) {
                content.push(`    • ${scopeGroup.scope_name.toUpperCase()}:`);
                scopeGroup.exclusions.forEach((exc) => {
                    content.push(`        - ${exc}`);
                });
                content.push('');
            }
        });
        
        if (groupedExclusions.length === 0 && generalExclusions.length === 0) {
            content.push('    No exclusions found in proposal.');
        }
        
        return content.join('\n');
    };
    
    const copyToClipboard = async () => {
        try {
            const content = generateExclusionsEmailContent();
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    const totalExclusions = generalExclusions.length + 
        groupedExclusions.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0);
    
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <FileText size={20} />
                        Proposal Exclusions
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        All exclusions extracted from the proposal document
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={copyToClipboard}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                            copied 
                                ? 'bg-green-600 text-white' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        {copied ? (
                            <>
                                <CheckCircle size={16} />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy size={16} />
                                Copy All
                            </>
                        )}
                    </button>
                </div>
            </div>
            
            {/* Stats */}
            <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">Total Exclusions:</span>
                    <span className="font-bold text-gray-900">{totalExclusions}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500">Scopes with Exclusions:</span>
                    <span className="font-bold text-gray-900">
                        {groupedExclusions.filter(g => g.exclusions?.length > 0).length}
                    </span>
                </div>
            </div>
            
            {/* General Exclusions */}
            {generalExclusions.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        General Exclusions
                    </h3>
                    <ul className="space-y-2">
                        {generalExclusions.map((exc, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                <span className="text-yellow-600 mt-1">•</span>
                                <span>{exc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            
            {/* Scope-Specific Exclusions */}
            {groupedExclusions.length > 0 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">Scope-Specific Exclusions</h3>
                    {groupedExclusions.map((scopeGroup, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                                <span className="font-semibold text-gray-800">{scopeGroup.scope_name}</span>
                                <span className="ml-2 text-sm text-gray-500">
                                    ({scopeGroup.exclusions?.length || 0} exclusions)
                                </span>
                            </div>
                            {scopeGroup.exclusions && scopeGroup.exclusions.length > 0 ? (
                                <ul className="p-4 space-y-2">
                                    {scopeGroup.exclusions.map((exc, excIdx) => (
                                        <li key={excIdx} className="flex items-start gap-2 text-sm text-gray-700">
                                            <span className="text-red-500 mt-1">•</span>
                                            <span>{exc}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="p-4 text-sm text-gray-400 italic">
                                    No specific exclusions for this scope
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {/* No Exclusions Message */}
            {totalExclusions === 0 && (
                <div className="text-center py-8 text-gray-400">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No exclusions found in the proposal.</p>
                    <p className="text-sm mt-2">Upload a proposal document to extract exclusions.</p>
                </div>
            )}
        </div>
    );
};

export default ExclusionsView;
